import { Routes, Route, Link, Navigate } from 'react-router-dom';
import { Dashboard, WebSocketTester, ArcheIconManager, BetaKeyManager } from './dev-console';
import {
  HomePage,
  BrowsePage,
  ArcheDetailPage,
  EntityDetailPage,
  CategoryPage,
  RunArchePage,
} from './marketplace';
import { ContributorDashboard } from './contributor';
import { CreditsPage } from './credits';
import { ThemeProvider } from '@/components/theme-provider';
import { Layout } from '@/components/Layout';
import { Toaster } from '@/components/ui/toaster';
import {
  AuthProvider,
  ProtectedRoute,
  LoginPage,
  SignupPage,
  ForgotPasswordPage,
  AccountSettingsPage,
  MyDataPage,
  SwitchDevUserPage,
  OAuthConsentPage,
  useAuth,
} from './auth';
import {
  ArcheInteractionPage,
  ConditionalProtectedRoute,
  PluginDevPreviewPage,
  ChatPage,
  BookingPage,
  BookingCancelPage,
} from './arche';
import { SharedConversationPage } from './shared';
import { TermsOfServicePage, PrivacyPolicyPage, AcceptableUsePolicyPage } from './legal';

function Home() {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="flex-1 flex items-center justify-center px-4 pb-20">
        <div className="text-center">
          <div className="animate-pulse">
            <div className="h-16 w-16 bg-muted rounded mx-auto mb-4" />
            <div className="h-8 bg-muted rounded w-48 mx-auto mb-4" />
            <div className="h-4 bg-muted rounded w-64 mx-auto" />
          </div>
        </div>
      </div>
    );
  }

  // Redirect authenticated users to the main arche interface
  if (isAuthenticated) {
    return <Navigate to="/a/arche_make_a_request" replace />;
  }

  return (
    <div className="flex-1 flex items-center justify-center px-4 pb-20">
      <div className="w-full max-w-sm">
        {/* Logo/Brand */}
        <div className="text-center mb-8">
          <img src="/fias-logo.png" alt="Fias" className="h-16 w-auto mx-auto mb-4 dark:invert" />
          <h1 className="text-2xl font-semibold text-foreground">Welcome to Fias</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Sign in to explore AI-powered applications
          </p>
        </div>

        {/* Card */}
        <div className="bg-card rounded-xl border shadow-sm p-6">
          <div className="space-y-4">
            <Link to="/auth/login" className="block">
              <button className="w-full h-11 bg-primary text-primary-foreground rounded-md font-medium hover:bg-primary/90 transition-colors">
                Sign in
              </button>
            </Link>
            <Link to="/auth/signup" className="block">
              <button className="w-full h-11 bg-secondary text-secondary-foreground rounded-md font-medium hover:bg-secondary/90 transition-colors">
                Create account
              </button>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

function App() {
  return (
    <ThemeProvider defaultTheme="dark" storageKey="fias-theme" attribute="class">
      <AuthProvider>
        <Toaster />
        <Routes>
          {/* Auth routes (public, no header) */}
          <Route path="/auth/login" element={<LoginPage />} />
          <Route path="/auth/signup" element={<SignupPage />} />
          <Route path="/auth/forgot-password" element={<ForgotPasswordPage />} />

          {/* Legal pages (public, no auth required) */}
          <Route path="/legal/terms" element={<TermsOfServicePage />} />
          <Route path="/legal/privacy" element={<PrivacyPolicyPage />} />
          <Route path="/legal/acceptable-use" element={<AcceptableUsePolicyPage />} />

          {/* Shared conversation (public, no auth required) */}
          <Route path="/shared/:shareId" element={<SharedConversationPage />} />

          {/* All other routes with Layout (header) */}
          <Route
            path="/"
            element={
              <Layout>
                <Home />
              </Layout>
            }
          />
          <Route
            path="/auth/settings"
            element={
              <ProtectedRoute>
                <Layout>
                  <AccountSettingsPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/auth/switch-user"
            element={
              <ProtectedRoute>
                <Layout>
                  <SwitchDevUserPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/my-data"
            element={
              <ProtectedRoute>
                <Layout>
                  <MyDataPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          {/* OAuth consent screen — auth required, with layout */}
          <Route
            path="/oauth/authorize"
            element={
              <ProtectedRoute>
                <Layout>
                  <OAuthConsentPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          {/* Plugin dev preview — auth required, no layout */}
          <Route
            path="/a/arche_plugins/dev-preview"
            element={
              <ProtectedRoute>
                <PluginDevPreviewPage />
              </ProtectedRoute>
            }
          />
          {/* Community chat lives at /chat */}
          <Route
            path="/chat"
            element={
              <ProtectedRoute>
                <Layout>
                  <ChatPage defaultChannelId="cch_global_fias_community" />
                </Layout>
              </ProtectedRoute>
            }
          />
          {/* Calendar booking pages — no auth, no layout */}
          <Route path="/a/arche_calendar/book/:slug" element={<BookingPage />} />
          <Route path="/a/arche_calendar/cancel/:slug" element={<BookingCancelPage />} />
          {/* Arche interaction route (conditionally protected - public arches don't require auth) */}
          <Route
            path="/a/:id/*"
            element={
              <ConditionalProtectedRoute>
                <ArcheInteractionPage />
              </ConditionalProtectedRoute>
            }
          />
          {/* Marketplace routes (protected) */}
          <Route
            path="/marketplace"
            element={
              <ProtectedRoute>
                <Layout>
                  <HomePage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/marketplace/browse"
            element={
              <ProtectedRoute>
                <Layout>
                  <BrowsePage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/marketplace/arches/:id"
            element={
              <ProtectedRoute>
                <Layout>
                  <ArcheDetailPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/marketplace/entities/:id"
            element={
              <ProtectedRoute>
                <Layout>
                  <EntityDetailPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/marketplace/arches/:id/run"
            element={
              <ProtectedRoute>
                <Layout>
                  <RunArchePage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/marketplace/categories/:slug"
            element={
              <ProtectedRoute>
                <Layout>
                  <CategoryPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          {/* Contributor routes (protected) */}
          <Route
            path="/contributor/dashboard"
            element={
              <ProtectedRoute>
                <Layout>
                  <ContributorDashboard />
                </Layout>
              </ProtectedRoute>
            }
          />
          {/* Credits routes (protected) */}
          <Route
            path="/credits"
            element={
              <ProtectedRoute>
                <Layout>
                  <CreditsPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          {/* Dev console routes (protected, admin only) */}
          <Route
            path="/dev-console"
            element={
              <ProtectedRoute requiredRoles={['admin']}>
                <Layout>
                  <Dashboard />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/dev-console/websocket-tester"
            element={
              <ProtectedRoute requiredRoles={['admin']}>
                <Layout>
                  <WebSocketTester />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/dev-console/arche-icons"
            element={
              <ProtectedRoute requiredRoles={['admin']}>
                <Layout>
                  <ArcheIconManager />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/dev-console/beta-keys"
            element={
              <ProtectedRoute requiredRoles={['admin']}>
                <Layout>
                  <BetaKeyManager />
                </Layout>
              </ProtectedRoute>
            }
          />
        </Routes>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
